import {dynamicLoadHandler} from "../../decorators/dynamic-load-handler.decorator";
import {DynamicLoadHandler} from "../dynamic-load-handler.interface";
import {html} from "lit";
import {Project} from "../../domain/entities/project.entity";
import {ProxyService} from "../../advanced-form/proxy.service";
import {container} from "tsyringe";
import {ProjectType} from "../../domain/entities/project-type.entity";
import {BOARD_TYPE_PROJECT, NOTIFICATION_DURATION} from "../../constants";
import {Subject} from "rxjs";
import {AbstractLitElement} from "../../components/abstract-lit.element";
import {MessageBroker} from "../../message-broker/message-broker";
import {Entity} from "../../domain/entities/entity";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../message-broker/payloads/entity-created.payload";
import {ListenerInfo} from "../../message-broker/listener-info.interface";
import {Issue} from "../../domain/entities/issue.entity";
import {CommandBus} from "../../services/command-bus.service";
import {BulkCommand} from "../../commands/bulk.command";
import {CreateIssueCommand} from "../../commands/create-issue.command";
import {UiHelper} from "../../helpers/ui.helper";
import {get, translate} from "lit-translate";
import {map} from "lit/directives/map.js";
import {DateHelper} from "../../helpers/date.helper";
import {UpdateIssueCommand} from "../../commands/update-issue.command";
import {EntityObjectArray} from "../../domain/entity-object-array";
import {DeleteIssueCommand} from "../../commands/delete-issue.command";
import {IssueRepository} from "../../domain/repositories/issue.repository";

type ProjectPayload = EntityCreatedPayload<Project | Entity> | EntityChangedPayload<Project | Entity>;

@dynamicLoadHandler({hookName: `ha-form-project.after-type`})
export class OnBoardTypeHandler implements DynamicLoadHandler {

    private _proxyService = container.resolve(ProxyService);

    public async filter(options: { entity: Project }): Promise<boolean> {
        if (!this._proxyService.has(options.entity.uid)) {
            return false;
        }

        const formPromise = this._proxyService.get(options.entity.uid);
        if (!formPromise) {
            return false;
        }

        const projectType = (await formPromise).getField<ProjectType>(`type`)?.value;
        if (!projectType) {
            return false;
        }

        return projectType.uid === BOARD_TYPE_PROJECT;
    }

    public render(component: AbstractLitElement, options: { entity: Project }): unknown {
        return html`
            <ha-plugin-timeframes .entity=${options.entity}
                                  .component=${component}
            ></ha-plugin-timeframes>
        `
    }

    public save(component: AbstractLitElement, options: {
        entity: Project,
        payload: { timeFrames?: EntityObjectArray<Issue>, type?: ProjectType }
    }): void {
        const disconnect$ = new Subject<void>();

        let listenerInfo;
        if (options.entity.isNew()) {
            if (!(`timeFrames` in options.payload && (options.payload.timeFrames ?? []).length > 0)) {
                return;
            }

            listenerInfo = <ListenerInfo<EntityCreatedPayload<Project | Entity>>>{
                messageType: `entity.created`,
                disconnect: disconnect$,
                filterFn: message => message.payload.entity.uid === options.entity.uid,
                method: async () => {
                    disconnect$.next();

                    const commands = (options.payload.timeFrames ?? [])
                        .filter(timeFrame => (timeFrame.isNew() || timeFrame.hasChanged))
                        .map(timeFrame => timeFrame.isNew() ? new CreateIssueCommand(timeFrame) : new UpdateIssueCommand(timeFrame));

                    const success = await container.resolve(CommandBus).dispatchCommand(new BulkCommand(commands));
                    if (success) {
                        UiHelper.notify({
                            title: get(`Zeitfenster angelegt`),
                            content: html`
                                <p>${translate(`Es wurde ${commands.length} Zeitfenster angelegt:`)}</p>
                                <ul>
                                    ${map(options.payload.timeFrames ?? [], timeFrame => html`
                                        <li>${DateHelper.intervalToLocalString(timeFrame.timeRange)}</li>
                                    `)}
                                </ul>
                            `,
                            closable: true,
                            duration: NOTIFICATION_DURATION,
                            level: "success"
                        });
                    } else {
                        UiHelper.notify({
                            title: get(`Zeitfenster anlegen fehlgeschlagen`),
                            content: html`
                                <p>${translate(`Es wurde kein Zeitfenster angelegt.`)}</p>
                            `,
                            closable: true,
                            duration: NOTIFICATION_DURATION,
                            level: "error"
                        });
                    }
                }
            };
        } else {
            listenerInfo = <ListenerInfo<EntityChangedPayload<Project | Entity>>>{
                messageType: `entity.changed`,
                disconnect: disconnect$,
                filterFn: message => message.payload.uid === options.entity.uid,
                method: async () => {
                    disconnect$.next();

                    if (`type` in options.payload) {
                        if (options.payload.type!.uid !== BOARD_TYPE_PROJECT) {
                            const timeFrameIssues = await container.resolve(IssueRepository).findAllMilestonesByProject(options.entity.uid);
                            if (timeFrameIssues.length < 1) {
                                return;
                            }

                            await container.resolve(CommandBus).dispatchCommand(new BulkCommand(timeFrameIssues.map(issue => new DeleteIssueCommand(issue))));
                            return;
                        }
                    }

                    if (!(`timeFrames` in options.payload && (options.payload.timeFrames ?? []).length > 0)) {
                        return;
                    }

                    const updateAndCreateTimeFrameCommands = (options.payload.timeFrames ?? [])
                        .filter(timeFrame => (timeFrame.isNew() || timeFrame.hasChanged))
                        .map(timeFrame => timeFrame.isNew() ? new CreateIssueCommand(timeFrame) : new UpdateIssueCommand(timeFrame));
                    const deleteTimeFrameCommands = (options.payload.timeFrames ?? new EntityObjectArray<Issue>()).allRemoved.map(removedIssue => new DeleteIssueCommand(removedIssue));
                    const commands = [
                        ...updateAndCreateTimeFrameCommands,
                        ...deleteTimeFrameCommands
                    ];

                    const success = await container.resolve(CommandBus).dispatchCommand(new BulkCommand(commands));
                    if (success) {
                        UiHelper.notify({
                            title: get(`Zeitfenster aktualisiert`),
                            content: html`
                                <p>
                                    ${translate(`Es wurde${commands.length > 1 ? `n` : ` ein`} Zeitfenster aktualisiert:`)}</p>
                                <ul>
                                    ${map(options.payload.timeFrames ?? [], timeFrame => html`
                                        <li>${DateHelper.intervalToLocalString(timeFrame.timeRange)}</li>
                                    `)}
                                </ul>
                            `,
                            closable: true,
                            duration: NOTIFICATION_DURATION,
                            level: "success"
                        });
                    } else {
                        UiHelper.notify({
                            title: get(`Zeitfenster aktualisieren fehlgeschlagen`),
                            content: html`
                                <p>${translate(`Es wurde kein Zeitfenster aktualisiert.`)}</p>
                            `,
                            closable: true,
                            duration: NOTIFICATION_DURATION,
                            level: "error"
                        });
                    }
                }
            };
        }
        // listenerInfo handles either created or changed payloads, never both. The broker hands a
        // listener only the messages whose type equals its messageType, so the branch-specific
        // filterFn and method above never see the other payload kind.
        container.resolve(MessageBroker).registerListener<ProjectPayload>(this, listenerInfo as ListenerInfo<ProjectPayload>);
    }

}